import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { applicationApi } from "../../services/applicationApi";
import { StatusBadge } from "../../components/StatusBadge";
import { EagleState } from "../../components/EagleState";
import { AccessDenied } from "../../components/AccessDenied";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";

export default function ApplicationsQueuePage() {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [accessDenied, setAccessDenied] = useState(false);

  const load = () => applicationApi.list()
    .then((r) => { setApplications(r.data); setAccessDenied(false); })
    .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); });
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    const { data } = await applicationApi.create();
    toast.success(t("applications.newApplication"));
    navigate(`/management/applications/${data.id}`);
  };

  if (accessDenied) return <AccessDenied resourceLabel={t("applications.title")} />;

  return (
    <div className="max-w-5xl" data-testid="applications-queue-page">
      <PageHelpBanner pageKey="applications-queue" title="How to use this page">
        <p>Start a management-initiated application here (e.g. for a walk-in candidate), or use this queue to track every application working through the multi-step wizard. Click one to open its wizard and continue where it left off.</p>
      </PageHelpBanner>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("applications.title")}</h2>
        <button data-testid="new-application-button" onClick={handleCreate} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all">
          <Plus size={16} /> {t("applications.newApplication")}
        </button>
      </div>

      <div className="space-y-3">
        {applications.map((app) => {
          const identity = app.identity || {};
          const name = [identity.legal_first_name, identity.legal_last_name].filter(Boolean).join(" ") || identity.personal_email || app.id;
          return (
            <div
              key={app.id}
              data-testid={`application-item-${app.id}`}
              onClick={() => navigate(`/management/applications/${app.id}`)}
              className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between cursor-pointer hover:shadow-md transition-shadow"
            >
              <div>
                <p className="font-semibold text-gray-800 text-sm">{name}</p>
                <p className="text-xs text-gray-500">{(app.employment || {}).position || "-"} · {(app.employment || {}).store_location || "-"}</p>
              </div>
              <StatusBadge status={app.status} />
            </div>
          );
        })}
        {applications.length === 0 && <EagleState variant="empty" title={t("applications.noApplications")} message={t("applications.title")} testId="applications-empty" />}
      </div>
    </div>
  );
}
